import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supplierService } from "~/services/purchasing";
import type { SupplierFilters, SupplierFormData } from "~/types/purchasing";

const QUERY_KEY = "suppliers";

export function useSuppliers(filters?: SupplierFilters) {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => supplierService.getAll(filters),
  });
}

export function useActiveSuppliers() {
  return useSuppliers({ is_active: true });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => supplierService.getById(id!),
    enabled: !!id,
  });
}

/** Set of supplier ids that have movements (POs, opening balances, prices) — can't be deleted. */
export function useSuppliersWithMovements() {
  return useQuery({
    queryKey: [QUERY_KEY, "with-movements"],
    queryFn: () => supplierService.getMovementSupplierIds(),
    select: (ids) => new Set(ids),
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SupplierFormData) => supplierService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<SupplierFormData>;
    }) => supplierService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => supplierService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useToggleSupplierActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      supplierService.toggleActive(id, is_active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
